import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Textarea,
  useToast,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Spinner,
  Center,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
} from '@chakra-ui/react';
import { MdEmail, MdCheckCircle, MdWarning } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUrl';

interface CompanySettings {
  companyName: string;
  supportEmail: string;
  supportPhone: string;
  officeAddress: string;
  websiteUrl: string;
  timezone: string;
  currency: string;
  logoUrl?: string;
}

interface EmailSettings {
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
  emailSignature: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  newLeadNotifications: boolean;
  demoReminders: boolean;
  onboardingUpdates: boolean;
  systemAlerts: boolean;
  contractRenewals: boolean;
}

interface GmailConnection {
  connected: boolean;
  email?: string;
  connectedAt?: string;
  lastSyncedAt?: string;
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const toast = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectingGmail, setConnectingGmail] = useState(false);

  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    companyName: '',
    supportEmail: '',
    supportPhone: '',
    officeAddress: '',
    websiteUrl: '',
    timezone: 'America/Toronto',
    currency: 'CAD',
  });

  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    fromName: '',
    fromEmail: '',
    replyToEmail: '',
    emailSignature: '',
  });

  const [notificationSettings, setNotificationSettings] =
    useState<NotificationSettings>({
      emailNotifications: true,
      newLeadNotifications: true,
      demoReminders: true,
      onboardingUpdates: true,
      systemAlerts: true,
      contractRenewals: true,
    });

  const [gmailConnection, setGmailConnection] = useState<GmailConnection>({
    connected: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      // Fetch company settings
      const companyResponse = await fetch(
        `${apiUrl}/getCompanySettings`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (companyResponse.ok) {
        try {
          const data = await companyResponse.json();
          setCompanySettings(data.settings || companySettings);
        } catch (jsonError) {
          console.warn('Invalid JSON response from getCompanySettings:', jsonError);
        }
      }

      // Fetch user-specific settings
      const settingsResponse = await fetch(
        `${apiUrl}/getSettings`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (settingsResponse.ok) {
        try {
          const data = await settingsResponse.json();
          if (data.settings) {
            if (data.settings.email) {
              setEmailSettings(data.settings.email);
            }
            if (data.settings.notifications) {
              setNotificationSettings(data.settings.notifications);
            }
          }
        } catch (jsonError) {
          console.warn('Invalid JSON response from getSettings:', jsonError);
          // Continue gracefully - settings are optional
        }
      }

      // Fetch Gmail connection status
      await fetchGmailConnection();
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: t('errors.fetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGmailConnection = async () => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/getGmailConnection`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        try {
          const data = await response.json();
          setGmailConnection(data.connection || { connected: false });
        } catch (jsonError) {
          console.warn('Invalid JSON response from getGmailConnection:', jsonError);
          setGmailConnection({ connected: false });
        }
      }
    } catch (error) {
      console.error('Error fetching Gmail connection:', error);
      // Silently fail - Gmail connection is optional
    }
  };

  const connectGmail = async () => {
    if (!user) return;
    
    try {
      setConnectingGmail(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      // Get OAuth URL from backend
      const response = await fetch(
        `${apiUrl}/initiateGmailAuth`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId: user.uid }),
        }
      );

      if (!response.ok) throw new Error('Failed to initiate Gmail auth');

      const data = await response.json();
      
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      const popup = window.open(
        data.authUrl,
        'gmail-oauth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      // Listen for OAuth callback
      const handleMessage = async (event: MessageEvent) => {
        if (event.data.type === 'GMAIL_AUTH_SUCCESS') {
          window.removeEventListener('message', handleMessage);
          popup?.close();
          
          toast({
            title: 'Gmail Connected',
            description: 'Successfully connected your Gmail account.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });

          // Refresh connection status
          await fetchGmailConnection();
        } else if (event.data.type === 'GMAIL_AUTH_ERROR') {
          window.removeEventListener('message', handleMessage);
          popup?.close();
          
          toast({
            title: 'Connection Failed',
            description: event.data.error || 'Failed to connect Gmail account.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      };

      window.addEventListener('message', handleMessage);

      // Cleanup if popup is closed manually
      const checkPopup = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', handleMessage);
          setConnectingGmail(false);
        }
      }, 1000);
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      toast({
        title: 'Connection Failed',
        description: 'Failed to connect Gmail account. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setConnectingGmail(false);
    }
  };

  const disconnectGmail = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/disconnectGmail`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId: user.uid }),
        }
      );

      if (!response.ok) throw new Error('Failed to disconnect Gmail');

      toast({
        title: 'Gmail Disconnected',
        description: 'Successfully disconnected your Gmail account.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setGmailConnection({ connected: false });
    } catch (error) {
      toast({
        title: 'Disconnection Failed',
        description: 'Failed to disconnect Gmail account. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const saveCompanySettings = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/updateCompanySettings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ settings: companySettings }),
        }
      );

      if (!response.ok) throw new Error('Failed to save company settings');

      toast({
        title: t('success.saved'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('errors.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const saveEmailSettings = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      const token = await user.getIdToken();

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/updateEmailSettings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ emailSettings }),
        }
      );

      if (!response.ok) throw new Error('Failed to save email settings');

      toast({
        title: t('success.saved'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('errors.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const saveNotificationSettings = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      const token = await user.getIdToken();

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/updateNotificationSettings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ notificationSettings }),
        }
      );

      if (!response.ok) throw new Error('Failed to save notification settings');

      toast({
        title: t('success.saved'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('errors.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={6}>
        {t('title')}
      </Heading>

      <Tabs>
        <TabList>
          <Tab>{t('tabs.company')}</Tab>
          <Tab>{t('tabs.email')}</Tab>
          <Tab>{t('tabs.notifications')}</Tab>
          <Tab>Integrations</Tab>
          <Tab>{t('tabs.security')}</Tab>
        </TabList>

        <TabPanels>
          {/* Company Settings */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">{t('company.title')}</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>{t('company.companyName')}</FormLabel>
                      <Input
                        value={companySettings.companyName}
                        onChange={(e) =>
                          setCompanySettings({
                            ...companySettings,
                            companyName: e.target.value,
                          })
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('company.websiteUrl')}</FormLabel>
                      <Input
                        type="url"
                        value={companySettings.websiteUrl}
                        onChange={(e) =>
                          setCompanySettings({
                            ...companySettings,
                            websiteUrl: e.target.value,
                          })
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('company.supportEmail')}</FormLabel>
                      <Input
                        type="email"
                        value={companySettings.supportEmail}
                        onChange={(e) =>
                          setCompanySettings({
                            ...companySettings,
                            supportEmail: e.target.value,
                          })
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('company.supportPhone')}</FormLabel>
                      <Input
                        type="tel"
                        value={companySettings.supportPhone}
                        onChange={(e) =>
                          setCompanySettings({
                            ...companySettings,
                            supportPhone: e.target.value,
                          })
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('company.timezone')}</FormLabel>
                      <Select
                        value={companySettings.timezone}
                        onChange={(e) =>
                          setCompanySettings({
                            ...companySettings,
                            timezone: e.target.value,
                          })
                        }
                      >
                        <option value="America/Toronto">
                          Eastern Time (Toronto)
                        </option>
                        <option value="America/Vancouver">
                          Pacific Time (Vancouver)
                        </option>
                        <option value="America/Chicago">
                          Central Time (Chicago)
                        </option>
                        <option value="America/New_York">
                          Eastern Time (New York)
                        </option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('company.currency')}</FormLabel>
                      <Select
                        value={companySettings.currency}
                        onChange={(e) =>
                          setCompanySettings({
                            ...companySettings,
                            currency: e.target.value,
                          })
                        }
                      >
                        <option value="CAD">CAD (Canadian Dollar)</option>
                        <option value="USD">USD (US Dollar)</option>
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>{t('company.officeAddress')}</FormLabel>
                    <Textarea
                      value={companySettings.officeAddress}
                      onChange={(e) =>
                        setCompanySettings({
                          ...companySettings,
                          officeAddress: e.target.value,
                        })
                      }
                      rows={3}
                    />
                  </FormControl>

                  <Divider />

                  <HStack justify="flex-end">
                    <Button
                      colorScheme="brand"
                      onClick={saveCompanySettings}
                      isLoading={saving}
                    >
                      {t('saveChanges')}
                    </Button>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Email Settings */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">{t('email.title')}</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>{t('email.fromName')}</FormLabel>
                      <Input
                        value={emailSettings.fromName}
                        onChange={(e) =>
                          setEmailSettings({
                            ...emailSettings,
                            fromName: e.target.value,
                          })
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('email.fromEmail')}</FormLabel>
                      <Input
                        type="email"
                        value={emailSettings.fromEmail}
                        onChange={(e) =>
                          setEmailSettings({
                            ...emailSettings,
                            fromEmail: e.target.value,
                          })
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('email.replyToEmail')}</FormLabel>
                      <Input
                        type="email"
                        value={emailSettings.replyToEmail}
                        onChange={(e) =>
                          setEmailSettings({
                            ...emailSettings,
                            replyToEmail: e.target.value,
                          })
                        }
                      />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>{t('email.emailSignature')}</FormLabel>
                    <Textarea
                      value={emailSettings.emailSignature}
                      onChange={(e) =>
                        setEmailSettings({
                          ...emailSettings,
                          emailSignature: e.target.value,
                        })
                      }
                      rows={6}
                      placeholder={t('email.signaturePlaceholder')}
                    />
                  </FormControl>

                  <Divider />

                  <HStack justify="flex-end">
                    <Button
                      colorScheme="brand"
                      onClick={saveEmailSettings}
                      isLoading={saving}
                    >
                      {t('saveChanges')}
                    </Button>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Notification Settings */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">{t('notifications.title')}</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      {t('notifications.emailNotifications')}
                      <Text fontSize="sm" color="gray.600">
                        {t('notifications.emailNotificationsHelp')}
                      </Text>
                    </FormLabel>
                    <Switch
                      isChecked={notificationSettings.emailNotifications}
                      onChange={(e) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          emailNotifications: e.target.checked,
                        })
                      }
                    />
                  </FormControl>

                  <Divider />

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      {t('notifications.newLeads')}
                    </FormLabel>
                    <Switch
                      isChecked={notificationSettings.newLeadNotifications}
                      onChange={(e) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          newLeadNotifications: e.target.checked,
                        })
                      }
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      {t('notifications.demoReminders')}
                    </FormLabel>
                    <Switch
                      isChecked={notificationSettings.demoReminders}
                      onChange={(e) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          demoReminders: e.target.checked,
                        })
                      }
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      {t('notifications.onboardingUpdates')}
                    </FormLabel>
                    <Switch
                      isChecked={notificationSettings.onboardingUpdates}
                      onChange={(e) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          onboardingUpdates: e.target.checked,
                        })
                      }
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      {t('notifications.systemAlerts')}
                    </FormLabel>
                    <Switch
                      isChecked={notificationSettings.systemAlerts}
                      onChange={(e) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          systemAlerts: e.target.checked,
                        })
                      }
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      {t('notifications.contractRenewals')}
                    </FormLabel>
                    <Switch
                      isChecked={notificationSettings.contractRenewals}
                      onChange={(e) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          contractRenewals: e.target.checked,
                        })
                      }
                    />
                  </FormControl>

                  <Divider />

                  <HStack justify="flex-end">
                    <Button
                      colorScheme="brand"
                      onClick={saveNotificationSettings}
                      isLoading={saving}
                    >
                      {t('saveChanges')}
                    </Button>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Integrations */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">Gmail Integration</Heading>
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Connect your Gmail account to automatically ingest and process payment emails.
                </Text>
              </CardHeader>
              <CardBody>
                <VStack spacing={6} align="stretch">
                  {gmailConnection.connected ? (
                    <Alert
                      status="success"
                      variant="subtle"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      textAlign="center"
                      borderRadius="md"
                      p={6}
                    >
                      <Icon as={MdCheckCircle} boxSize="40px" mb={4} />
                      <AlertTitle mb={2} fontSize="lg">
                        Gmail Connected
                      </AlertTitle>
                      <AlertDescription maxWidth="sm">
                        <VStack spacing={2}>
                          <HStack>
                            <Icon as={MdEmail} />
                            <Text fontWeight="medium">{gmailConnection.email}</Text>
                          </HStack>
                          {gmailConnection.connectedAt && (
                            <Text fontSize="sm">
                              Connected on {new Date(gmailConnection.connectedAt).toLocaleDateString()}
                            </Text>
                          )}
                          {gmailConnection.lastSyncedAt && (
                            <Text fontSize="sm" color="gray.600">
                              Last synced: {new Date(gmailConnection.lastSyncedAt).toLocaleString()}
                            </Text>
                          )}
                        </VStack>
                      </AlertDescription>
                      <Button
                        mt={4}
                        colorScheme="red"
                        variant="outline"
                        onClick={disconnectGmail}
                        isLoading={saving}
                      >
                        Disconnect Gmail
                      </Button>
                    </Alert>
                  ) : (
                    <Alert
                      status="warning"
                      variant="subtle"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      textAlign="center"
                      borderRadius="md"
                      p={6}
                    >
                      <Icon as={MdWarning} boxSize="40px" mb={4} />
                      <AlertTitle mb={2} fontSize="lg">
                        Gmail Not Connected
                      </AlertTitle>
                      <AlertDescription maxWidth="sm" mb={4}>
                        Connect your Gmail account to enable automated payment email processing.
                        We'll securely access only payment-related emails.
                      </AlertDescription>
                      <Button
                        colorScheme="brand"
                        leftIcon={<Icon as={MdEmail} />}
                        onClick={connectGmail}
                        isLoading={connectingGmail}
                        loadingText="Opening Gmail OAuth..."
                      >
                        Connect Gmail Account
                      </Button>
                    </Alert>
                  )}

                  <Divider />

                  <Box>
                    <Heading size="sm" mb={3}>
                      What we access:
                    </Heading>
                    <VStack align="start" spacing={2} pl={4}>
                      <HStack>
                        <Badge colorScheme="green">Read Only</Badge>
                        <Text fontSize="sm">Payment notification emails</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme="green">Read Only</Badge>
                        <Text fontSize="sm">E-transfer notifications</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme="blue">Secure</Badge>
                        <Text fontSize="sm">OAuth 2.0 authentication</Text>
                      </HStack>
                    </VStack>
                  </Box>

                  <Box bg="gray.50" p={4} borderRadius="md">
                    <Heading size="sm" mb={2}>
                      Privacy & Security
                    </Heading>
                    <Text fontSize="sm" color="gray.700">
                      We only access emails related to payment processing. Your credentials are never stored,
                      and you can revoke access at any time. All data is encrypted in transit and at rest.
                    </Text>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Security Settings */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">{t('security.title')}</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Text color="gray.600">
                    {t('security.description')}
                  </Text>
                  <Button colorScheme="blue" variant="outline">
                    {t('security.enableMFA')}
                  </Button>
                  <Button colorScheme="orange" variant="outline">
                    {t('security.changePassword')}
                  </Button>
                  <Button colorScheme="red" variant="outline">
                    {t('security.viewAuditLog')}
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default SettingsPage;
